import { CSRF_FIELD_NAME, CSRF_HEADER_NAME } from '~/config/consts'

// クッキー名はサーバ側 (app/server/csrf.server.ts) と共有する
const CSRF_COOKIE_PATTERN = new RegExp(`(?:^|;\\s*)${CSRF_FIELD_NAME}=([^;]*)`)

const MUTATING_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH']

// Check if a URL is same-origin to avoid leaking CSRF token to third parties
function isSameOrigin(input: RequestInfo | URL): boolean {
  try {
    const url = input instanceof Request ? new URL(input.url) : new URL(input.toString(), window.location.origin)
    return url.origin === window.location.origin
  } catch {
    // Relative URLs are same-origin
    return true
  }
}

function readCsrfToken(): string | undefined {
  return document.cookie.match(CSRF_COOKIE_PATTERN)?.[1]
}

/**
 * 同一オリジンの POST/PUT/DELETE/PATCH に CSRF トークンを付与した fetch 引数を返す。
 *
 * トークンは必ず `init.headers` 側に載せること。`new Request(input, { headers })` で
 * Request オブジェクトに載せ直してはいけない。
 *
 * Sentry の fetch 計装は Request を渡されると、トレースヘッダを足した `init.headers` を
 * 組み立てて `fetch(request, { headers })` の形で呼び直す（`@sentry/core` の fetch.js）。
 * `init.headers` は Request 側のヘッダを丸ごと置き換えるので、Request に載せたトークンは
 * そこで捨てられる。SENTRY_DSN 未設定のローカルでは起きないため、ローカル確認では気づけない。
 *
 * react-router は 8.5 から single fetch が `fetch(request)` と Request を渡すようになった。
 * 8.4 までは `fetch(url, init)` だったので、この分岐自体が通っていなかった。
 *
 * @see https://github.com/eiicon-company/auba-issues/issues/104
 */
export function withCsrfToken(
  input: RequestInfo | URL,
  init?: RequestInit
): [RequestInfo | URL, RequestInit | undefined] {
  const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase()
  if (!MUTATING_METHODS.includes(method) || !isSameOrigin(input)) {
    return [input, init]
  }

  const csrfToken = readCsrfToken()
  if (!csrfToken) {
    return [input, init]
  }

  const headers = new Headers(input instanceof Request ? input.headers : undefined)
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => {
      headers.set(key, value)
    })
  }
  headers.set(CSRF_HEADER_NAME, csrfToken)

  return [input, { ...(init || {}), headers }]
}

/** Attach CSRF token to same-origin mutating fetch requests (POST/PUT/DELETE/PATCH) */
export function setupCsrfInterceptor() {
  const originalFetch = window.fetch.bind(window)
  window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    return originalFetch(...withCsrfToken(input, init))
  }
}
